import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { LinkedFrenchHomeForm } from '@main/types';

type FormDataContext = {
  formData?: LinkedFrenchHomeForm;
  reusableFormData?: LinkedFrenchHomeForm;
  setFormData: Dispatch<SetStateAction<LinkedFrenchHomeForm | undefined>>;
  setReusableFormData: Dispatch<SetStateAction<LinkedFrenchHomeForm | undefined>>;
  clearFormDataOnNewPolicySelection: (policy: InsurancePolicy) => void;
};

const FormDataContext = createContext<FormDataContext | undefined>(undefined);

export function useFormData(): FormDataContext {
  const context = useContext(FormDataContext);

  if (context === undefined) {
    throw new Error('useFormData must be used within a FormDataProvider');
  }

  return context;
}

export const FormDataContextProvider = ({ children }: PropsWithChildren) => {
  const [formData, setFormData] = useState<FormDataContext['formData']>();
  // Data that can be reused as default values for any selected insurance
  const [reusableFormData, setReusableFormData] = useState<FormDataContext['reusableFormData']>();

  const clearFormDataOnNewPolicySelection = useCallback(
    (policy: InsurancePolicy) => {
      // Remove any stored non-reusable data when a new policy is selected
      if (formData && formData?.externalId !== policy.insurance.externalId) {
        setFormData(undefined);
      }
    },
    [formData, setFormData],
  );

  const initialValues = useMemo(
    () => ({
      formData,
      reusableFormData,
      setFormData,
      setReusableFormData,
      clearFormDataOnNewPolicySelection,
    }),
    [
      formData,
      reusableFormData,
      setFormData,
      setReusableFormData,
      clearFormDataOnNewPolicySelection,
    ],
  );

  return <FormDataContext.Provider value={initialValues}>{children}</FormDataContext.Provider>;
};
